// FILE: src/player/interimScormApi.js
// A minimal SCORM 2004 `API_1484_11` on the player page, so a SCORM package
// in the lesson iframe can tell the time engine where the student is
// (plan WP-3 minimum dwell): `cmi.location` → the current slide,
// `cmi.completion_status = completed` → ask the server to complete,
// `cmi.score.scaled` → the score. Values live in memory only.
//
// Interim: WP-4 replaces this with scorm-again and a server-persisted CMI
// model (plan AD-1). Keep the callbacks — WP-4 feeds the same ones.

const NO_ERROR = "0";

export function createInterimScormApi({
  onInitialize,
  onLocation,
  onCompletion,
  onScore,
  onCommit,
  onTerminate
} = {}) {
  const cmi = {
    "cmi.entry": "ab-initio",
    "cmi.mode": "normal",
    "cmi.credit": "credit",
    "cmi.completion_status": "unknown",
    "cmi.success_status": "unknown",
    "cmi.location": "",
    "cmi.suspend_data": ""
  };
  let initialized = false;
  let terminated = false;
  let lastError = NO_ERROR;

  const ok = () => {
    lastError = NO_ERROR;
    return "true";
  };
  const fail = (code) => {
    lastError = code;
    return "false";
  };

  return {
    Initialize() {
      if (initialized) return fail("103");
      initialized = true;
      onInitialize?.();
      return ok();
    },
    Terminate() {
      if (!initialized || terminated) return fail("112");
      terminated = true;
      onTerminate?.();
      return ok();
    },
    GetValue(key) {
      if (!initialized) {
        lastError = "122";
        return "";
      }
      lastError = NO_ERROR;
      return cmi[key] ?? "";
    },
    SetValue(key, value) {
      if (!initialized || terminated) return fail("132");
      const v = String(value);
      cmi[key] = v;
      if (key === "cmi.location") onLocation?.(parseInt(v, 10));
      if (key === "cmi.completion_status" && v === "completed") onCompletion?.();
      if (key === "cmi.score.scaled") {
        const scaled = Number(v);
        if (Number.isFinite(scaled)) onScore?.(Math.round(scaled * 10000) / 100);
      }
      return ok();
    },
    Commit() {
      if (!initialized || terminated) return fail("142");
      onCommit?.();
      return ok();
    },
    GetLastError: () => lastError,
    GetErrorString: (code) => (code === NO_ERROR ? "No error" : `Error ${code}`),
    GetDiagnostic: (code) => String(code || lastError)
  };
}

/** Puts the API on `win` for the iframe to find; returns the uninstall function. */
export function installInterimScormApi(win, callbacks) {
  const api = createInterimScormApi(callbacks);
  win.API_1484_11 = api;
  return () => {
    if (win.API_1484_11 === api) delete win.API_1484_11;
  };
}
